import { useAuth0 } from '@auth0/auth0-react'
import { useLingui } from '@lingui/react/macro'
import React, { useEffect, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'

import { useCognito } from './CognitoProvider'
import { redesignAuthorizationParams } from './helper'
import { useKeycloak } from './KeycloakProvider'

const LoginPageContainer = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  background-color: #111;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 500;
  font-family: 'Manrope', 'Helvetica Neue', Arial, Helvetica, sans-serif;
  color: white;
  background-image: radial-gradient(
    rgba(255, 255, 255, 0.3) 1px,
    transparent 0
  );
  background-size: 6rem 6rem;
  background-position: fixed;
`
const GlobalStyle = createGlobalStyle`
  

  h1 {
    color: #eeeeee;
    font-size: 1.4rem;
    letter-spacing: 0.04rem;
    font-weight: 300;
    text-align: center;
    margin: 0;
  }

  p {
    color: #cccccc;
    font-size: 1rem;
    letter-spacing: 0.04rem;
    line-height: 1.4;
    font-weight: 300;
    text-align: center;
    margin: 0;
  }
  
  input {
    color: white !important;
    font-weight: bold;
  }

  .google-logo {
    margin-right: 1rem;
  }
  
`

const SpotlightContainer = styled.div`
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const Spotlight = styled.div<{ $top: number; $left: number }>`
  position: absolute;
  z-index: 0;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: 40rem;
  height: 40rem;
  background: radial-gradient(#e5e5e5 1px, transparent 0);
  background-size: 6rem 6rem;
  background-repeat: repeat;
  background-attachment: fixed;
  border-radius: 50%;
  box-shadow: inset 0 0 100px #111;
`

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  align-items: center;
  position: relative;
  height: 100%;
`

const ContentContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  padding: 3rem 6rem 5rem 6rem;
  background-color: transparent;
  /* max-width: 41rem; */
  margin: auto 0;
  width: 100%;
`

const ContentWrap = styled.div`
  display: flex;

  flex-direction: column;
  gap: 5rem;
  min-width: 464px;
  height: 640px;
`

const LoginHeader = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
`

const Logo = styled.img`
  width: 80%;

  cursor: pointer;
`

const GeckoLogo = styled.div`
  display: flex;
  justify-content: center;

  text-align: center;
  width: 100%;

  @media (min-height: 48rem) {
    position: absolute;
    bottom: 4rem;
    left: 0;
  }

  a svg * {
    transition: fill 0.2s ease;
  }

  a:hover svg * {
    fill: #fff;
  }
`

const LoginButton = styled.button`
  border-radius: 100px;
  font-size: 19.2px;
  font-weight: 700;
  line-height: 1.1;
  color: #111;
  padding: 1rem 2rem;
  width: 100%;
  background: white;

  &:hover {
    background: #eeff00;
    color: #111;
    border-color: #eeff00;
  }

  &:disabled {
    background: #858c20;
    border-color: #858c20;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 1rem;
`

type LoginPageProps = {
  provider: string
}

export const LoginPage: React.FC<LoginPageProps> = ({
  provider,
}: LoginPageProps) => {
  const { t } = useLingui()
  let loginFunction: VoidFunction
  const { loginWithRedirect } = useAuth0()
  const { sendToCognitoLogin } = useCognito()
  const { sendToKeycloakLogin } = useKeycloak()
  if (provider === 'auth0') {
    loginFunction = () => {
      loginWithRedirect({
        appState: { returnTo: window.location.href },
        authorizationParams: redesignAuthorizationParams(),
      })
    }
  } else if (provider === 'cognito') {
    loginFunction = () => sendToCognitoLogin()
  } else if (provider === 'keycloak') {
    loginFunction = () => sendToKeycloakLogin()
  } else {
    throw new Error(`Invalid login type passed to LoginPage: ${provider}`)
  }

  const [spotlightPosition, setSpotlightPosition] = useState({
    top: window.innerHeight / 2 - 160,
    left: window.innerWidth / 2 - 320,
  })

  useEffect(() => {
    // we dont want to add bootstrap everywhere, so we add it here
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href =
      'https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css'

    document.head.appendChild(link)

    return () => {
      document.head.removeChild(link)
    }
  }, [])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setSpotlightPosition({ top: e.clientY - 160, left: e.clientX - 160 })
    }

    document.addEventListener('mousemove', handleMouseMove)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  return (
    <LoginPageContainer>
      <GlobalStyle />
      <SpotlightContainer>
        <Spotlight
          $top={spotlightPosition.top}
          $left={spotlightPosition.left}
        />
      </SpotlightContainer>
      <PageWrapper>
        <ContentContainer>
          <ContentWrap>
            <LoginHeader>
              <Logo src='https://uploads-ssl.webflow.com/63349fc0ae8d7c3feaab48a9/65e08075bdffd934fed2ffc0_Cantilever-Logo.svg' />
            </LoginHeader>
            <ButtonWrapper>
              <LoginButton onClick={loginFunction}>{t`Log In`}</LoginButton>
            </ButtonWrapper>

            <GeckoLogo>
              <a href='https://www.geckorobotics.com'>
                <svg
                  width='72'
                  height='30'
                  viewBox='0 0 72 30'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M60.5677 17.5697C62.1345 17.5697 63.4047 16.308 63.4047 14.7516C63.4047 13.1952 62.1345 11.9335 60.5677 11.9335C59.0008 11.9335 57.7306 13.1952 57.7306 14.7516C57.7306 16.308 59.0008 17.5697 60.5677 17.5697Z'
                    fill='#CCCCCC'
                  />
                  <path
                    d='M10.7296 11.8665C9.52696 11.8665 8.66423 12.6942 8.66423 13.8126C8.66423 14.9309 9.54895 15.7586 10.7296 15.7586C11.9102 15.7586 12.8169 14.9309 12.8169 13.8126C12.8169 12.6942 11.9092 11.8665 10.7296 11.8665Z'
                    fill='#CCCCCC'
                  />
                  <path
                    d='M23.9414 11.5975C22.6928 11.5975 21.6951 12.4025 21.3542 13.6549H26.5505C26.1876 12.3799 25.189 11.5975 23.9404 11.5975H23.9414Z'
                    fill='#CCCCCC'
                  />
                  <path
                    d='M0 0V29.0785H72V0H0ZM17.8013 10.2555H16.4028C15.8579 10.2555 15.4271 10.5235 15.3131 10.9491C15.8809 11.7542 16.2208 12.7159 16.2208 13.8126C16.2208 16.6976 13.8606 18.8457 10.7286 18.8457C10.1388 18.8457 9.57094 18.7787 9.02612 18.622C8.68623 18.8457 8.50428 19.159 8.50428 19.4941C8.50428 20.0084 8.93515 20.3444 9.54795 20.3444H13.1558C15.755 20.3444 17.7993 22.2313 17.7993 24.7282H14.1765C14.0855 23.9902 13.4507 23.4975 12.52 23.4975H8.91215C6.66587 23.4975 5.19034 22.2225 5.19034 20.3217C5.19034 19.1137 5.80315 17.951 6.77883 17.3697C5.82614 16.475 5.25832 15.2226 5.25832 13.8135C5.25832 10.9285 7.59557 8.7804 10.7276 8.7804C11.6123 8.7804 12.43 8.95974 13.1558 9.25041C13.4967 8.01972 14.6533 7.23735 16.2418 7.23735H17.7993V10.2574L17.8013 10.2555ZM30.0004 15.8246H21.3772C21.786 17.0997 22.8977 17.882 24.3502 17.882C25.6668 17.882 26.7105 17.278 27.2553 16.607L29.4796 18.5077C28.254 19.8724 26.3936 20.7218 24.2603 20.7218C20.5834 20.7218 17.8153 18.1274 17.8153 14.6826C17.8153 11.2379 20.4705 8.77745 23.9883 8.77745C27.5062 8.77745 30.0704 11.3275 30.0704 14.794C30.0704 15.1073 30.0474 15.532 30.0024 15.8227L30.0004 15.8246ZM37.0802 17.5697C38.1698 17.5697 39.0775 17.011 39.5534 16.1606L42.4354 17.883C41.3688 19.6281 39.4174 20.7238 37.0802 20.7238C33.5173 20.7238 30.8391 18.151 30.8391 14.7516C30.8391 11.3522 33.5173 8.77942 37.0802 8.77942C39.4174 8.77942 41.3688 9.87512 42.4354 11.6202L39.5534 13.3425C39.0765 12.4922 38.1688 11.9335 37.0802 11.9335C35.4467 11.9335 34.2431 13.1415 34.2431 14.7516C34.2431 16.3616 35.4457 17.5697 37.0802 17.5697ZM50.8318 20.4548L47.7907 16.7863L46.928 17.7037V20.4548H43.6151V4.3503H46.928V13.2972L50.7408 9.04743H55.0294L50.128 14.3042L55.1884 20.4558H50.8318V20.4548ZM60.5667 20.7228C57.0038 20.7228 54.3256 18.15 54.3256 14.7506C54.3256 11.3512 57.0038 8.77843 60.5667 8.77843C64.1295 8.77843 66.8077 11.3512 66.8077 14.7506C66.8077 18.15 64.1295 20.7228 60.5667 20.7228Z'
                    fill='#CCCCCC'
                  />
                </svg>
              </a>
            </GeckoLogo>
          </ContentWrap>
        </ContentContainer>
      </PageWrapper>
    </LoginPageContainer>
  )
}
